/**
 * Quotation PDF Generator
 * Mirrors the invoice PDF layout so users get a consistent branded experience.
 * Honors the same invoice_settings (logo, company details, theme, border design,
 * signature, default T&C) so branding is configured once.
 */
import jsPDF from 'jspdf';
import {
  PDF_THEMES,
  loadImageAsDataUrl,
  getImageFormat,
  truncateTextForPdf,
  drawBorderDesign
} from './invoicePdfGenerator';
import { formatDate } from './currency';

const INR = (n) =>
  `Rs. ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const generateQuotationPDF = async (
  { quotation, admin, venue, invoiceSettings },
  onSuccess
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const settings = invoiceSettings || {};
  const company = settings.companyDetails || {};
  const colorTheme = settings.pdfTemplate?.colorTheme || 'sage_cream';
  const borderDesign = settings.pdfTemplate?.borderDesign || 'botanical';
  const t = PDF_THEMES[colorTheme] || PDF_THEMES.sage_cream;

  const logoUrl = company.logo_url;
  const signatureUrl = settings.signature?.url;

  const [logoDataUrl, signatureDataUrl] = await Promise.all([
    loadImageAsDataUrl(logoUrl),
    loadImageAsDataUrl(signatureUrl)
  ]);

  // ===== BACKGROUND =====
  doc.setFillColor(...t.bg);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  // ===== BORDER =====
  drawBorderDesign(doc, borderDesign, t, pageWidth, pageHeight);

  // ===== HEADER — LEFT: logo + company; RIGHT: title + number =====
  const centerX = pageWidth / 2;
  let logoEndX = 20;

  if (logoDataUrl) {
    try {
      const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = logoDataUrl;
      });
      const maxW = 28, maxH = 22;
      const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
      const w = img.naturalWidth * ratio;
      const h = img.naturalHeight * ratio;
      doc.addImage(logoDataUrl, getImageFormat(logoDataUrl), 20, 16, w, h);
      logoEndX = 20 + w + 3;
    } catch (e) {
      // logo failure non-fatal
    }
  }

  // Company name + details
  let compY = 20;
  const companyName =
    company.name || venue?.name || admin?.venueName || quotation.venue_name || 'COMPANY';
  doc.setFontSize(12);
  doc.setTextColor(...t.dark);
  doc.setFont(undefined, 'bold');
  doc.text(companyName.toUpperCase(), logoEndX, compY);
  compY += 5;

  doc.setFontSize(7);
  doc.setTextColor(...t.muted);
  doc.setFont(undefined, 'normal');

  const addrSource = company.address || venue?.address;
  if (addrSource) {
    const addrLines = doc.splitTextToSize(addrSource, 75);
    doc.text(addrLines.slice(0, 2), logoEndX, compY);
    compY += addrLines.slice(0, 2).length * 3.5;
  }
  if (company.gstin) {
    doc.text(`GSTIN: ${company.gstin}`, logoEndX, compY);
    compY += 3.5;
  }
  const phone = company.phone || admin?.phone;
  if (phone) {
    doc.text(`Ph: ${phone}`, logoEndX, compY);
    compY += 3.5;
  }
  const email = company.email || admin?.email;
  if (email) {
    doc.text(email, logoEndX, compY);
  }

  // Right side: Quotation title + number + date
  doc.setFontSize(9);
  doc.setTextColor(...t.muted);
  doc.setFont(undefined, 'normal');
  doc.text(quotation.quotation_number || '', pageWidth - 20, 18, { align: 'right' });

  doc.setFontSize(26);
  doc.setTextColor(...t.dark);
  doc.setFont(undefined, 'italic');
  doc.text('Quotation', pageWidth - 20, 30, { align: 'right' });

  doc.setFontSize(8);
  doc.setTextColor(...t.muted);
  doc.setFont(undefined, 'normal');
  doc.text(`ISSUE DATE: ${formatDate(quotation.created_at) || '-'}`, pageWidth - 20, 40, {
    align: 'right'
  });
  if (quotation.valid_until) {
    doc.text(`VALID UNTIL: ${formatDate(quotation.valid_until)}`, pageWidth - 20, 45, {
      align: 'right'
    });
  }
  doc.text(`STATUS: ${String(quotation.status || 'draft').toUpperCase()}`, pageWidth - 20, 50, {
    align: 'right'
  });

  // ===== QUOTATION TO =====
  const headerEndY = 58;
  doc.setDrawColor(...t.primary);
  doc.setLineWidth(0.3);
  doc.line(20, headerEndY, pageWidth - 20, headerEndY);

  doc.setFontSize(10);
  doc.setTextColor(...t.dark);
  doc.setFont(undefined, 'bold');
  doc.text('QUOTATION TO', 25, headerEndY + 8);

  let custY = headerEndY + 15;
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...t.dark);
  doc.text((quotation.client_name || '-').toUpperCase(), 25, custY);
  custY += 5;

  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...t.muted);

  if (quotation.client_phone) {
    doc.text(`Ph: ${quotation.client_phone}`, 25, custY);
    custY += 3.5;
  }
  if (quotation.client_email) {
    doc.text(quotation.client_email, 25, custY);
    custY += 3.5;
  }
  if (quotation.event_type) {
    doc.text(`Event: ${quotation.event_type}`, 25, custY);
    custY += 3.5;
  }
  if (quotation.event_date_from) {
    const dates =
      quotation.event_date_to && quotation.event_date_to !== quotation.event_date_from
        ? `${formatDate(quotation.event_date_from)} to ${formatDate(quotation.event_date_to)}`
        : formatDate(quotation.event_date_from);
    doc.text(`Event Date: ${dates}`, 25, custY);
    custY += 3.5;
  }
  if (quotation.guest_count) {
    doc.text(`Guests: ${quotation.guest_count}`, 25, custY);
    custY += 3.5;
  }

  // Right column: Venue name
  let metaY = headerEndY + 15;
  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...t.muted);
  if (quotation.venue_name) {
    doc.text(`Venue: ${quotation.venue_name}`, pageWidth - 25, metaY, { align: 'right' });
    metaY += 4;
  }

  const toEndY = Math.max(custY + 3, metaY + 3);
  doc.setDrawColor(...t.primary);
  doc.line(20, toEndY, pageWidth - 20, toEndY);

  // ===== ITEMS TABLE =====
  const tableHeaderY = toEndY + 4;
  doc.setFillColor(...t.tableHeader);
  doc.rect(20, tableHeaderY, pageWidth - 40, 10, 'F');

  doc.setFontSize(9);
  doc.setTextColor(...t.dark);
  doc.setFont(undefined, 'bold');
  doc.text('#', 24, tableHeaderY + 7);
  doc.text('DESCRIPTION', 32, tableHeaderY + 7);
  doc.text('QTY', 115, tableHeaderY + 7);
  doc.text('RATE', 140, tableHeaderY + 7);
  doc.text('AMOUNT', pageWidth - 25, tableHeaderY + 7, { align: 'right' });

  doc.setFont(undefined, 'normal');
  doc.setTextColor(...t.muted);
  let rowY = tableHeaderY + 18;
  const rowH = 7;

  const allItems = [
    ...(quotation.spaces || []).map((s) => ({
      name: s.space_name,
      type: s.space_type,
      qty: s.quantity,
      unit: 'nos',
      rate: s.unit_price,
      total: s.total_price,
      days: 1,
      images: (s.images && s.images.length)
        ? s.images
        : (s.image_url ? [{ url: s.image_url, orientation: s.image_orientation || 'landscape' }] : [])
    })),
    ...(quotation.amenities || []).map((a) => ({
      name: a.amenity_name,
      type: a.category,
      qty: a.quantity,
      unit: a.unit || 'nos',
      rate: a.unit_price,
      total: a.total_price,
      days: a.days || 1,
      images: (a.images && a.images.length)
        ? a.images
        : (a.image_url ? [{ url: a.image_url, orientation: a.image_orientation || 'landscape' }] : [])
    }))
  ];

  // Pre-fetch every item image in parallel so we don't stall the render loop.
  // For each item we collect up to 2 images, so we build a flat list + remember
  // the mapping from (item index, slot index) → flat index.
  const itemImageBase = `${process.env.REACT_APP_BACKEND_URL}/api/quotations/item-image/`;
  const flatLoads = [];
  const itemImageSlots = []; // itemImageSlots[i] = array of flat indexes for item i
  allItems.forEach((it) => {
    const slots = [];
    (it.images || []).slice(0, 2).forEach((img) => {
      slots.push(flatLoads.length);
      flatLoads.push(loadImageAsDataUrl(`${itemImageBase}${img.url}`));
    });
    itemImageSlots.push(slots);
  });
  const flatImageData = await Promise.all(flatLoads);

  // ATM card dimensions (ISO/IEC 7810 ID-1): 85.60mm × 53.98mm
  // Scaled down ~10% so multiple items can share a page comfortably.
  const PHOTO_SCALE = 0.9;
  const CARD_W_LANDSCAPE = 85.6 * PHOTO_SCALE;   // ≈ 77.0 mm
  const CARD_H_LANDSCAPE = 53.98 * PHOTO_SCALE;  // ≈ 48.6 mm
  const CARD_W_PORTRAIT = 53.98 * PHOTO_SCALE;   // ≈ 48.6 mm
  const CARD_H_PORTRAIT = 85.6 * PHOTO_SCALE;    // ≈ 77.0 mm
  const CARD_GAP = 4; // mm between two cards on the same row

  const addNewPageForItems = () => {
    doc.addPage();
    drawBorderDesign(doc, borderDesign, t, pageWidth, pageHeight);
    doc.setFillColor(...t.bg);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    rowY = 30;
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...t.muted);
  };

  allItems.forEach((it, i) => {
    // Build the list of cards actually loaded for this item
    const slotIdxs = itemImageSlots[i] || [];
    const loaded = slotIdxs
      .map((flatIdx, slotIdx) => {
        const data = flatImageData[flatIdx];
        if (!data) return null;
        const orientation = it.images?.[slotIdx]?.orientation === 'portrait' ? 'portrait' : 'landscape';
        return {
          data,
          orientation,
          w: orientation === 'landscape' ? CARD_W_LANDSCAPE : CARD_W_PORTRAIT,
          h: orientation === 'landscape' ? CARD_H_LANDSCAPE : CARD_H_PORTRAIT
        };
      })
      .filter(Boolean);

    // Figure out layout for the image block:
    //   - 0 images: nothing extra
    //   - 1 image: single card
    //   - 2 images: try side-by-side (total width <= usable). If not, stack vertically.
    const imgX = 32;
    const usableW = pageWidth - 25 - imgX; // right margin = 25, usableW = space under description column
    let imgBlockH = 0;
    let layout = 'none';
    if (loaded.length === 1) {
      imgBlockH = loaded[0].h + 4;
      layout = 'single';
    } else if (loaded.length === 2) {
      const naturalTotal = loaded[0].w + CARD_GAP + loaded[1].w;
      if (naturalTotal <= usableW) {
        imgBlockH = Math.max(loaded[0].h, loaded[1].h) + 4;
        layout = 'side';
      } else {
        // Try shrinking both proportionally to fit side-by-side.
        // Only downscale if the resulting size stays reasonably readable
        // (>= 65% of natural ATM card size); otherwise stack vertically.
        const scale = (usableW - CARD_GAP) / (loaded[0].w + loaded[1].w);
        if (scale >= 0.65) {
          loaded[0].w *= scale; loaded[0].h *= scale;
          loaded[1].w *= scale; loaded[1].h *= scale;
          imgBlockH = Math.max(loaded[0].h, loaded[1].h) + 4;
          layout = 'side';
        } else {
          imgBlockH = loaded[0].h + loaded[1].h + CARD_GAP + 4;
          layout = 'stacked';
        }
      }
    }

    const blockH = rowH + imgBlockH;
    // Page-break: keep item row + its photo block together. Only reserve a small
    // bottom gutter (~20 mm) so items pack as tightly as possible. The totals
    // block below will push itself to a new page if needed.
    if (rowY + blockH > pageHeight - 20) {
      addNewPageForItems();
    }

    // Row text
    const label = it.type ? `${it.name} (${it.type})` : it.name;
    doc.text(String(i + 1), 24, rowY);
    doc.text(truncateTextForPdf(label, 50), 32, rowY);
    const qtyTxt = it.days && it.days > 1 ? `${it.qty} ${it.unit} x ${it.days}d` : `${it.qty} ${it.unit}`;
    doc.text(qtyTxt, 115, rowY);
    doc.text(INR(it.rate), 140, rowY);
    doc.text(INR(it.total), pageWidth - 25, rowY, { align: 'right' });
    rowY += rowH;

    // Photo block
    if (layout !== 'none') {
      doc.setDrawColor(...t.primary);
      doc.setLineWidth(0.2);
      try {
        if (layout === 'single') {
          const c = loaded[0];
          doc.addImage(c.data, getImageFormat(c.data), imgX, rowY, c.w, c.h);
          doc.rect(imgX, rowY, c.w, c.h);
          rowY += c.h + 4;
        } else if (layout === 'side') {
          const c1 = loaded[0];
          const c2 = loaded[1];
          doc.addImage(c1.data, getImageFormat(c1.data), imgX, rowY, c1.w, c1.h);
          doc.rect(imgX, rowY, c1.w, c1.h);
          const x2 = imgX + c1.w + CARD_GAP;
          doc.addImage(c2.data, getImageFormat(c2.data), x2, rowY, c2.w, c2.h);
          doc.rect(x2, rowY, c2.w, c2.h);
          rowY += Math.max(c1.h, c2.h) + 4;
        } else if (layout === 'stacked') {
          const c1 = loaded[0];
          const c2 = loaded[1];
          doc.addImage(c1.data, getImageFormat(c1.data), imgX, rowY, c1.w, c1.h);
          doc.rect(imgX, rowY, c1.w, c1.h);
          rowY += c1.h + CARD_GAP;
          doc.addImage(c2.data, getImageFormat(c2.data), imgX, rowY, c2.w, c2.h);
          doc.rect(imgX, rowY, c2.w, c2.h);
          rowY += c2.h + 4;
        }
      } catch (e) {
        // image render failure is non-fatal
      }
    }
  });

  // Estimate space needed for totals block (up to 4 lines @ 7mm + 12mm grand total band + padding)
  // and for the terms/notes block (~40mm if either present, else just the line).
  const hasTerms = !!(quotation.terms_and_conditions || settings.defaultTermsConditions);
  const hasNotes = !!quotation.notes;
  const totalsLines = 1 + (quotation.discount_amount > 0 ? 1 : 0) + (quotation.gst_amount > 0 ? 1 : 0);
  const totalsBlockH = 10 + totalsLines * 7 + 14;
  const termsBlockH = (hasTerms || hasNotes) ? 35 : 6;
  const reservedBelow = totalsBlockH + termsBlockH + 30; // +30mm for signature/footer
  if (rowY + reservedBelow > pageHeight - 10) {
    addNewPageForItems();
  }

  doc.setDrawColor(...t.primary);
  doc.line(20, rowY + 2, pageWidth - 20, rowY + 2);

  // ===== TOTALS (right side) =====
  let totY = rowY + 10;
  doc.setFontSize(9);
  doc.setTextColor(...t.muted);
  doc.setFont(undefined, 'normal');

  doc.text('SUBTOTAL', 130, totY);
  doc.text(INR(quotation.subtotal), pageWidth - 25, totY, { align: 'right' });
  totY += 7;

  if (quotation.discount_amount > 0) {
    const discLbl = quotation.discount_percent
      ? `DISCOUNT (${quotation.discount_percent}%)`
      : 'DISCOUNT';
    doc.text(discLbl, 130, totY);
    doc.text(`-${INR(quotation.discount_amount)}`, pageWidth - 25, totY, { align: 'right' });
    totY += 7;
  }

  if (quotation.gst_amount && quotation.gst_amount > 0) {
    doc.text(`GST (${quotation.gst_percent || 0}% ${quotation.gst_type || 'exclusive'})`, 130, totY);
    doc.text(INR(quotation.gst_amount), pageWidth - 25, totY, { align: 'right' });
    totY += 7;
  }

  // Grand total band
  totY += 2;
  doc.setFillColor(...t.primary);
  doc.rect(125, totY - 5, pageWidth - 20 - 125, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(11);
  doc.text('GRAND TOTAL', 130, totY + 2);
  doc.text(INR(quotation.grand_total), pageWidth - 25, totY + 2, { align: 'right' });
  totY += 12;

  // ===== TERMS & NOTES (left side) =====
  const termsY = Math.max(totY + 6, rowY + 15);
  doc.setDrawColor(...t.primary);
  doc.line(20, termsY, pageWidth - 20, termsY);

  const termsText = quotation.terms_and_conditions || settings.defaultTermsConditions;
  if (termsText) {
    doc.setFontSize(8);
    doc.setTextColor(...t.dark);
    doc.setFont(undefined, 'bold');
    doc.text('Terms & Conditions', 25, termsY + 8);

    doc.setFont(undefined, 'normal');
    doc.setTextColor(...t.muted);
    doc.setFontSize(7);
    const splitTerms = doc.splitTextToSize(termsText, 80);
    doc.text(splitTerms.slice(0, 6), 25, termsY + 14);
  }

  if (quotation.notes) {
    const notesX = pageWidth / 2 + 5;
    doc.setFontSize(8);
    doc.setTextColor(...t.dark);
    doc.setFont(undefined, 'bold');
    doc.text('Notes', notesX, termsY + 8);

    doc.setFont(undefined, 'normal');
    doc.setTextColor(...t.muted);
    doc.setFontSize(7);
    const splitNotes = doc.splitTextToSize(quotation.notes, 70);
    doc.text(splitNotes.slice(0, 5), notesX, termsY + 14);
  }

  // ===== SIGNATURE =====
  const signature = settings.signature;
  if (signature?.showOnInvoice !== false && (signatureDataUrl || signature?.name)) {
    const sigX = pageWidth - 80;
    const sigY = pageHeight - 55;

    if (signatureDataUrl) {
      try {
        const sigImg = new Image();
        sigImg.src = signatureDataUrl;
        const sigMaxW = 40, sigMaxH = 18;
        const sigRatio = Math.min(
          sigMaxW / (sigImg.naturalWidth || sigMaxW),
          sigMaxH / (sigImg.naturalHeight || sigMaxH)
        );
        const sigW = (sigImg.naturalWidth || sigMaxW) * sigRatio;
        const sigH = (sigImg.naturalHeight || sigMaxH) * sigRatio;
        doc.addImage(
          signatureDataUrl,
          getImageFormat(signatureDataUrl),
          sigX + 5,
          sigY,
          sigW,
          sigH
        );
      } catch (e) {
        // signature failure non-fatal
      }
    }

    doc.setDrawColor(...t.dark);
    doc.setLineWidth(0.3);
    doc.line(sigX, sigY + 16, sigX + 45, sigY + 16);

    doc.setFontSize(8);
    doc.setTextColor(...t.dark);
    doc.setFont(undefined, 'normal');
    if (signature?.name) {
      doc.text(signature.name, sigX + 22, sigY + 22, { align: 'center' });
    }
    doc.setFontSize(7);
    doc.setTextColor(...t.muted);
    if (signature?.designation) {
      doc.text(signature.designation, sigX + 22, sigY + 27, { align: 'center' });
    }
  }

  // Footer thank-you
  doc.setFontSize(9);
  doc.setTextColor(...t.primary);
  doc.setFont(undefined, 'italic');
  doc.text('Thank you for considering us!', centerX, pageHeight - 18, { align: 'center' });

  const filename = `Quotation_${quotation.quotation_number || quotation.id}.pdf`;
  doc.save(filename);
  if (onSuccess) onSuccess(filename);
};
